import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Circle,
  Clock,
  Award,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent } from "@/app/global-components/card";

interface TenderStepperProps {
  status: string;
}

export const TenderStepper: React.FC<TenderStepperProps> = ({ status }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const authUser = useSelector((state: RootState) => state.auth.authUser);

  const statuses = [
    {
      label: t("Phases.annons"),
      name: "Opened",
      icon: <Clock className="w-6 h-6" />,
      subText: t("Phases.publishDetails"),
      color: "bg-purple-500",
      completedColor: "bg-purple-600",
    },
    {
      label: t("status.closed"),
      name: "Closed",
      icon: <Circle className="w-6 h-6" />,
      subText: t("Phases.reviewOffers"),
      color: "bg-orange-500",
      completedColor: "bg-orange-600",
    },
    {
      label: t("status.accepted"),
      name: "Accepted",
      icon: <Award className="w-6 h-6" />,
      subText: t("Phases.selectOffer"),
      color: "bg-green-500",
      completedColor: "bg-green-600",
    },
    {
      label: t("status.finished"),
      name: "Finished",
      icon: <CheckCircle2 className="w-6 h-6" />,
      subText: t("Phases.completeProcess"),
      color: "bg-emerald-500",
      completedColor: "bg-emerald-600",
    },
  ];

  const statusesSeller = [
    {
      label: t("Phases.annons"),
      name: "Opened",
      icon: <Clock className="w-5 h-5" />,
      subText: t("Phases.publishDetails"),
      color: "bg-purple-500",
      completedColor: "bg-purple-600",
    },
    {
      label: t("status.closed"),
      name: "Closed",
      icon: <Circle className="w-5 h-5" />,
      subText: t("Phases.reviewOffers"),
      color: "bg-orange-500",
      completedColor: "bg-orange-600",
    },
    {
      label: t("status.accepted"),
      name: "Accepted",
      icon: <Award className="w-5 h-5" />,
      subText: t("Phases.selectOffer"),
      color: "bg-green-500",
      completedColor: "bg-green-600",
    },
    {
      label: t("status.finished"),
      name: "Finished",
      icon: <CheckCircle2 className="w-5 h-5" />,
      subText: t("Phases.completeProcess"),
      color: "bg-emerald-500",
      completedColor: "bg-emerald-600",
    },
  ];

  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const getFilteredStatuses = () => {
    if (authUser?.role === ROLE.BUYER) {
      return statuses;
    } else {
      return statusesSeller;
    }
  };

  const filteredStatuses = getFilteredStatuses();

  useEffect(() => {
    if (status) {
      const index = filteredStatuses.findIndex((item) => item.name === status);
      setActiveStep(index !== -1 ? index : 0);
    }
  }, [status, filteredStatuses]);

  useEffect(() => {
    if (containerRef.current) {
      const activeElement = containerRef.current.children[
        activeStep
      ] as HTMLElement;
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeStep]);

  return (
    <Card className="mb-8 border-0 bg-gradient-to-r from-gray-50 to-white">
      <CardContent className="py-10 px-2 md:px-10">
        <div className="flex flex-col items-center justify-center w-full">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {t("tenders.details")}
            </h2>
            <p className="text-gray-600 text-lg">{t("tenders.information")}</p>
          </div>

          {/* Stepper */}
          <div className="w-full flex flex-col items-center">
            <div
              ref={containerRef}
              className="flex w-full max-w-5xl justify-between items-center relative z-10"
            >
              {filteredStatuses.map((item, index) => {
                const isActive = index === activeStep;
                const isCompleted = index < activeStep;
                return (
                  <div
                    key={item.name}
                    className="flex-1 flex flex-col items-center relative min-w-[120px]"
                  >
                    {/* Step Circle */}
                    <div className="relative z-10 flex flex-col items-center">
                      <motion.div
                        className={`
                          flex items-center justify-center w-14 h-14 rounded-full border-4 transition-all duration-300
                          ${
                            isActive
                              ? "border-purple-500 bg-white ring-4 ring-purple-100"
                              : isCompleted
                              ? "border-emerald-500 bg-emerald-500 text-white"
                              : "border-gray-300 bg-white text-gray-400"
                          }
                        `}
                      
                        whileHover={{ scale: 1.07 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-7 h-7 text-emerald-500" />
                        ) : (
                          item.icon
                        )}
                      </motion.div>
                      {/* Step Label */}
                      <div className="mt-3 text-center">
                        <span
                          className={`block font-bold text-base md:text-lg transition-colors duration-300 ${
                            isActive
                              ? "text-purple-700"
                              : isCompleted
                              ? "text-emerald-700"
                              : "text-gray-400"
                          }`}
                        >
                          {item.label}
                        </span>
                        <span
                          className={`block text-xs md:text-sm mt-1 ${
                            isActive
                              ? "text-purple-500"
                              : isCompleted
                              ? "text-emerald-500"
                              : "text-gray-400"
                          }`}
                        >
                          {item.subText}
                        </span>
                        {isActive && (
                          <motion.div
                            className="mx-auto mt-2 w-2 h-2 bg-emerald-500 rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </div>
                    </div>
                    {/* Connection Line (after the step, except for the last step) */}
                    {index !== filteredStatuses.length - 1 && (
                      <div
                        className={`absolute top-1/2 left-1/2 w-full h-2 z-0 flex items-center ${
                          locale === "ar" ? "rtl-line" : ""
                        }`}
                        style={{
                          transform: "translateY(-50%)",
                          left: locale === "ar" ? "auto" : "50%",
                          right: locale === "ar" ? "50%" : "auto",
                        }}
                      >
                        <div
                          className={`h-1 w-full rounded-full transition-all duration-300 ${
                            isCompleted
                              ? "bg-gradient-to-r from-purple-500 to-emerald-500"
                              : "bg-gray-200"
                          }`}
                        ></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Progress Bar */}
            <div className="w-full max-w-5xl mt-8">
              <div className="w-full bg-gray-200 rounded-full h-1">
                <motion.div
                  className="bg-gradient-to-r from-purple-500 to-emerald-500 h-1 rounded-full"
                  initial={{ width: 0 }}
                  animate={{
                    width: `${
                      ((activeStep + 1) / filteredStatuses.length) * 100
                    }%`,
                  }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span>
                  {t("status.step")} {activeStep + 1}
                </span>
                <span>
                  {t("status.of")} {filteredStatuses.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TenderStepper;
